import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import '@algolia/autocomplete-theme-classic';

export class AlgoliaAutocompleteElement extends HTMLElement {
  static get observedAttributes() { return ['data-algolia']; }

  constructor() {
    super();
    this._initialized = false;
    this._instance = null;
    this._config = null;

    this._mount = document.createElement('div');
    this._mount.className = 'autocomplete-root';

    this.labelInputPlaceholder = document.getElementById('input-placeholder').getAttribute('data-text');
    this.labelCategory = document.getElementById('label-category').getAttribute('data-text');
    this.labelProduct = document.getElementById('label-product').getAttribute('data-text');
    this.labelFavoriteCategories = document.getElementById('label-favorite-categories').getAttribute('data-text');
    this.labelRecentProducts = document.getElementById('label-recent-products').getAttribute('data-text');
    this.labelLastSearched = document.getElementById('label-last-searched').getAttribute('data-text');
    this.labelRemoveHistory = document.getElementById('label-remove-history').getAttribute('data-text');
  }

  connectedCallback() {
    
    if (this._initialized) return;
    this._initialized = true;

    if (!this._mount.isConnected) this.appendChild(this._mount);

    // Parse config from attribute (backward compatible with original file)
    this._config = this._parseConfig(this.getAttribute('data-algolia'));

    // Initialize
    this._initAutocomplete();
  }

  disconnectedCallback() {
    // this._destroy();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'data-algolia' && oldVal !== newVal) {
      const next = this._parseConfig(newVal);
      if (JSON.stringify(next) !== JSON.stringify(this._config)) {
        this._config = next;
        this._reinit();
      }
    }
  }

  /** Public API: replace config programmatically */
  setConfig(cfg) {
    this._config = cfg;
    this.setAttribute('data-algolia', JSON.stringify(cfg));
  }

  /** Public API: get underlying autocomplete instance */
  get instance() { return this._instance; }

  _parseConfig(str) {
    try { return str ? JSON.parse(str) : {}; } catch { return {}; }
  }

  _reinit() {
    this._destroy();
    this._initAutocomplete();
  }

  _destroy() {
    try { this._instance?.destroy?.(); } catch {}
    this._instance = null;
    // Ensure previous DOM is removed to avoid double-render on re-init
    if (this._mount) this._mount.innerHTML = '' 
  }

  _initAutocomplete() {
    if (this._instance) return;

    const cfg = this._config || {};
    const indexCfg = cfg.indexConfigs || {};

    const APP_ID = indexCfg.application_id;
    const API_KEY = indexCfg.public_key;
    const PRODUCTS_INDEX = indexCfg.products;
    const CATEGORIES_INDEX = indexCfg.categories;

    // Helpers
    const getCurrentLocale = () => {
        const path = window.location.pathname;
        const pathParts = path.split('/').filter(Boolean);

        if (pathParts.length > 0) {
            const firstPart = pathParts[0];
            if (firstPart === 'it' || firstPart === 'en' || firstPart === 'fr') {
                return firstPart;
            }
        }

        // Default locale
        return 'de';
    }

    // Translations
    const labelLastSearched = document.getElementById('label-last-searched').getAttribute('data-text');
    const labelInputPlaceholder = document.getElementById('input-placeholder').getAttribute('data-text');
    const labelCategory = document.getElementById('label-category').getAttribute('data-text');
    const labelProduct = document.getElementById('label-product').getAttribute('data-text');
    const labelFavoriteCategories = document.getElementById('label-favorite-categories').getAttribute('data-text');
    const labelRecentProducts = document.getElementById('label-recent-products').getAttribute('data-text');
    const labelRemoveHistory = document.getElementById('label-remove-history').getAttribute('data-text');
    const labelNoProducts = document.getElementById('label-no-products').getAttribute('data-text');
    const currentLocale = getCurrentLocale();
    

    if (!APP_ID || !API_KEY || !PRODUCTS_INDEX || !CATEGORIES_INDEX) {
      console.warn('[<algolia-autocomplete>] Missing required Algolia config.');
      return;
    }

    const searchClient = algoliasearch(APP_ID, API_KEY);

    // === Local storage helpers (recently viewed) ===
    const RV_KEY_PRODUCTS = 'RECENT_VIEWED_PRODUCTS';
    const RV_KEY_CATEGORIES = 'RECENT_VIEWED_CATEGORIES';
    const RV_LIMIT = 5;

    const loadRecent = (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } };
    const saveRecent = (items, key) => { try { localStorage.setItem(key, JSON.stringify(items.slice(0, RV_LIMIT))); } catch {} };

    const pushRecent = (hit) => {
      const items = loadRecent(RV_KEY_PRODUCTS);
      const filtered = items.filter((i) => i.objectID !== hit.objectID);
      filtered.unshift({
        objectID: hit.objectID,
        name: hit.post_title_de,
        url: hit.permalink_de || '#',
        image: hit.thumbnail_url || '',
      });
      saveRecent(filtered, RV_KEY_PRODUCTS);
    };

    const pushRecentCategory = (hit) => {
      const items = loadRecent(RV_KEY_CATEGORIES);
      const filtered = items.filter((i) => i.objectID !== hit.objectID);
      filtered.unshift({ name: hit.name_de, url: hit.permalink_de || '#' });
      saveRecent(filtered, RV_KEY_CATEGORIES);
    };

    // === Source builders ===
    const recentlyViewedSource = (items) => ({
      sourceId: 'recentlyViewed',
      getItems: () => items,
      templates: {
        header({ html }) { return html`<span class="tw:font-bold">${labelRecentProducts}</span>`; },
        item({ item, html }) {
          return html`
            <a class="aa-ItemLink" title="${item.name}" href="${item.url || '#'}">
              <div class="aa-ItemContent">
                <div class="aa-ItemIcon aa-ItemIcon--picture">
                  <img src="${item.image || ''}" alt="${item.name || ''}" loading="lazy" />
                </div>
                <div class="aa-ItemContentBody">
                  <div class="aa-ItemContentTitle">${item.name}</div>
                  <div class="aa-ItemContentDescription">${item.price != null ? `CHF ${Number(item.price).toFixed(2)}` : ''}</div>
                </div>
              </div>
            </a>`;
        },
        noResults() { return ''; },
      },
    });

    const removeStorage = (event) => {
      localStorage.removeItem(RV_KEY_CATEGORIES);
      this._instance.refresh();
    }

    const recentlyViewedCategory = (items) => ({
      sourceId: 'recentlyViewedCategory',
      getItems: () => items,
      templates: {
        header({ html }) { return html`
            <div class="tw:flex tw:justify-between tw:items-center">
              <span class="tw:font-bold">${labelLastSearched}</span>
              <button id="remove-rvc" onclick="${(event)=> removeStorage() }" class="tw:font-light tw:text-primary tw:hover:text-primary-dark tw:min-h-4 tw:h-4 tw:leading-none">${labelRemoveHistory}</button>
            </div>
          `; },
        item({ item, html }) {
          return html`
            <a class="aa-ItemLink" title="${item.name}" href="${item.url || '#'}">
              <div class="aa-ItemContent"><div class="aa-ItemContentBody"><div class="aa-ItemContentTitle">${item.name}</div></div></div>
            </a>`;
        },
        noResults() { return ''; },
      },
    });

    const popularCategoriesSource = () => {
      const popularCategories = Array.isArray(cfg.products) ? cfg.products : [];
      return {
        sourceId: 'popularCategories',
        getItems({ query }) {
          if (query.length > 2) {
            const q = String(query).toLowerCase();
            return popularCategories
              .filter((it) => (it.algolia_product_title || '').toLowerCase().includes(q))
              .sort((a, b) => (b.count || 0) - (a.count || 0))
              .slice(0, 8);
          }
          return popularCategories
            .slice()
            .sort((a, b) => (b.count || 0) - (a.count || 0))
            .slice(0, 6);
        },
        templates: {
          header({ state, html }) { return html`<span class="tw:font-bold">${labelFavoriteCategories}</span>`; },
          item({ item, html }) { 
            let url =  item.algolia_product_url;
            if (currentLocale != 'de') {
              url = `/${currentLocale}${item.algolia_product_url}`
            }

            return html`
              <a class="aa-ItemLink" title="${item.algolia_url_title ? item.algolia_url_title : item.algolia_product_title}" href="${ url || '#'}">
                <div class="aa-ItemWrapper"><div class="aa-ItemContent"><div class="aa-ItemContentBody"><div class="">${item.algolia_product_title}</div></div></div></div>
              </a>`;
          },
        },
        onSelect({ item, setQuery, refresh }) { setQuery(item.algolia_product_title); refresh(); },
      };
    };

    // Category Source
    const categorySource = () => ({
      sourceId: 'categories',
      onSelect({ item }) { pushRecentCategory(item); if (item.permalink_de) window.location.assign(item.permalink_de); },
      getItems({ query }) {
        if (!query) return [];
        if (query.length > 2) {
          return getAlgoliaResults({
            searchClient,
            queries: [{ indexName: CATEGORIES_INDEX, query, params: { hitsPerPage: 5, attributesToHighlight: [] } }],
          });
        }
      },
      templates: {
        header({ html }) { return html`<span class="aa-SourceHeader tw:font-bold">${labelCategory}</span>`; },
        item({ item, html }) { 
          const permalink = `permalink_${currentLocale}`;
          const titleAttr = `name_${currentLocale}`;

          return html`
          <a class="aa-ItemLink" title="${item[titleAttr]}" href="${item[permalink] || '#'}">
            <div class="aa-ItemWrapper"><div class="aa-ItemContent"><div class="aa-ItemContentBody"><div class="aa-ItemContentTitle">${item[titleAttr]}</div></div></div></div>
          </a>`; },
      },
    });

    // Product Source
    const productSource = () => ({
      sourceId: 'products',
      onSelect({ item }) { pushRecent(item); if (item.permalink_de) window.location.assign(item.permalink_de); },
      getItems({ query }) {
        if (!query) return [];
        if (query.length > 2) {
          return getAlgoliaResults({
            searchClient,
            queries: [{ indexName: PRODUCTS_INDEX, query, params: { hitsPerPage: 8, attributesToSnippet: ['description:10'], filters:'visibility:"visible" OR visibility:"search"', snippetEllipsisText: '…' } }],
          });
        }
      },
      templates: {
        header({ html }) { return html`<span class="aa-SourceHeader tw:font-bold">${labelProduct}</span>`; },
        item({ item, html }) {
          const permalink = `permalink_${currentLocale}`;
          const titleAttr = `post_title_${currentLocale}`;

          return html`
            <a class="aa-ItemLink" title="${item[titleAttr] || ''}" href="${item[permalink] || '#'}">
              <div class="aa-ItemContent">
                <div class="aa-ItemIcon aa-ItemIcon--picture">
                  <img src="${item.thumbnail_url || ''}" alt="${item[titleAttr] || ''}" loading="lazy" />
                </div>
                <div class="aa-ItemContentBody"><div class="">${item[titleAttr] || ''}</div></div>
              </div>
            </a>`;
        },
        noResults({ html }) { return html`<div class="aa-ItemContentTitle">${labelNoProducts}</div>`; },
      },
    });

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'qs-with-rs-example',
      limit: 3,
      transformSource({ source }) {
        return {
          ...source,
          onSelect({ item, event }) {
            if (event?.type === 'click') {
              window.location.href = '/?s=' + encodeURIComponent(item.label) + '&post_type=product';
            }
          },
          templates: {
            ...source.templates,
            header({ state }) {
              if (state.query) return null;
              const container = document.createElement('div');
              const title = document.createElement('span');
              title.className = 'aa-SourceHeaderTitle';
              title.textContent = 'Your searches';
              const line = document.createElement('div');
              line.className = 'aa-SourceHeaderLine';
              container.appendChild(title); container.appendChild(line);
              return container;
            },
          },
        };
      },
    });

    const presetQuery = new URLSearchParams(window.location.search).get("s") || "";
    
    // Create the autocomplete instance
    this._instance = autocomplete({
      container: this._mount,
      placeholder: labelInputPlaceholder,
      plugins: [recentSearchesPlugin],
      openOnFocus: true,
      autoFocus: true,
      detachedMediaQuery: '',
      debug: false,
      initialState: {
        query: presetQuery
      },
      onStateChange({ state }) {
        const cancelBtn = document.querySelector('.aa-DetachedCancelButton');
        const magnBtn = document.querySelector('.aa-SubmitButton');
        if (magnBtn) magnBtn.innerHTML = `<svg class="tw:h-5 tw:w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path class="tw:h-5 tw:w-5" d="M480 272C480 317.9 465.1 360.3 440 394.7L566.6 521.4C579.1 533.9 579.1 554.2 566.6 566.7C554.1 579.2 533.8 579.2 521.3 566.7L394.7 440C360.3 465.1 317.9 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272zM272 416C351.5 416 416 351.5 416 272C416 192.5 351.5 128 272 128C192.5 128 128 192.5 128 272C128 351.5 192.5 416 272 416z"/></svg>`;
        if (cancelBtn && cancelBtn.textContent.trim() !== 'Close') {
          cancelBtn.innerHTML = '<svg class="tw:h-6 tw:w-6 tw:text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M183.1 137.4C170.6 124.9 150.3 124.9 137.8 137.4C125.3 149.9 125.3 170.2 137.8 182.7L275.2 320L137.9 457.4C125.4 469.9 125.4 490.2 137.9 502.7C150.4 515.2 170.7 515.2 183.2 502.7L320.5 365.3L457.9 502.6C470.4 515.1 490.7 515.1 503.2 502.6C515.7 490.1 515.7 469.8 503.2 457.3L365.8 320L503.1 182.6C515.6 170.1 515.6 149.8 503.1 137.3C490.6 124.8 470.3 124.8 457.8 137.3L320.5 274.7L183.1 137.4z"/></svg>';
        }

        const input = document.querySelector('.aa-Input');
        if (input && !input.dataset.enterListenerAdded) {
          input.dataset.enterListenerAdded = "true";

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
               const query = input.value.trim();
              if (query.length > 0) {
                const url = `/?s=${encodeURIComponent(query)}&post_type=product`;
                window.location.href = url;
              }
            }
          });
        }
      },
      getSources({ query, state }) {
        const sources = [];
        const hasQuery = !!state.query && state.query.trim().length > 2;
        if (!hasQuery) {
          const recent = loadRecent(RV_KEY_PRODUCTS);
          const recentCategories = loadRecent(RV_KEY_CATEGORIES);
          if (recentCategories.length > 0) sources.push(recentlyViewedCategory(recentCategories));
          if (recent.length > 0) sources.push(recentlyViewedSource(recent));
          if (sources.length > 0) return sources;
          return [popularCategoriesSource()];
        }
        sources.push(categorySource(), productSource());
        return sources;
      },
    });

    // tweak detached trigger icon if present
    const magnifyingBtn = this._mount.querySelector('.aa-DetachedSearchButtonIcon');
    if (magnifyingBtn) {
      magnifyingBtn.innerHTML = `<span class="tw:text-black"><svg class="tw:h-5 tw:w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path class="tw:h-5 tw:w-5"  d="M480 272C480 317.9 465.1 360.3 440 394.7L566.6 521.4C579.1 533.9 579.1 554.2 566.6 566.7C554.1 579.2 533.8 579.2 521.3 566.7L394.7 440C360.3 465.1 317.9 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272zM272 416C351.5 416 416 351.5 416 272C416 192.5 351.5 128 272 128C192.5 128 128 192.5 128 272C128 351.5 192.5 416 272 416z"/></svg></span>`;
    }

    const btnSearch = document.querySelectorAll('.open-search');
    if (btnSearch.length) {
      btnSearch.forEach(btn => {
         btn.addEventListener('click', () => {
          const builtin = document.querySelector('.aa-DetachedSearchButton');
          if (builtin) builtin.click();
        
          // Bugfix 
          const offCanvas = document.querySelector('.mfp-wrap');
          if (offCanvas) {
            offCanvas.removeAttribute("tabindex");  
          }
        });
      });
    }
  }
}

// Define once
if (!customElements.get('algolia-autocomplete')) {
  customElements.define('algolia-autocomplete', AlgoliaAutocompleteElement);
}